import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Building2, Mail, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Separator } from "../components/ui/separator";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) toast.error(res.error);
    else toast.success("Benvenuto in LOOK OFF MARKET");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual side */}
      <div className="hidden lg:flex relative bg-[hsl(222,47%,11%)] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="font-heading text-lg font-semibold tracking-tight">LOOK OFF MARKET</div>
          </div>
          <div className="mt-16 max-w-md">
            <h1 className="font-heading text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.05]">
              Le opportunità immobiliari, <span className="text-primary">prima del mercato</span>.
            </h1>
            <p className="mt-6 text-slate-300 text-base leading-relaxed">
              Trova, valuta e chiudi trattative off-market con una piattaforma progettata per professionisti e investitori esigenti.
            </p>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-400 tabular-nums">
          © {new Date().getFullYear()} LOOK OFF MARKET · CRM immobiliare
        </div>
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(59,130,246,0.4), transparent 40%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.25), transparent 45%)" }} />
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md border-border/60 shadow-sm p-8">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-semibold tracking-tight">LOOK OFF MARKET</span>
          </div>

          <h2 className="font-heading text-2xl font-semibold tracking-tight">Accedi al tuo workspace</h2>
          <p className="text-sm text-muted-foreground mt-1">Bentornato. Inserisci le tue credenziali.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-9" placeholder="nome@azienda.it" data-testid="login-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-9" placeholder="••••••••" data-testid="login-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="login-submit-button">
              {busy ? "Accesso in corso…" : (<>Accedi <ArrowRight className="w-4 h-4" /></>)}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">oppure</span>
            <Separator className="flex-1" />
          </div>

          <Button variant="outline" className="w-full gap-2" onClick={googleLogin} data-testid="login-google-button">
            <GoogleIcon /> Continua con Google
          </Button>

          <p className="text-sm text-muted-foreground text-center mt-8">
            Nuovo su LOOK OFF MARKET?{" "}
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="login-register-link">
              Crea un account
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z" /><path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.5 12c0-.72.12-1.43.34-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" /></svg>
  );
}
